import type { S3Credentials } from "./ClientS3Manager";

export class SecureCredentialStorage {
  private static readonly STORAGE_KEY = "s3_credentials_";
  private static readonly TIMESTAMP_KEY = "s3_credentials_timestamp_";
  private static readonly CREDENTIAL_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

  static async storeCredentials(
    craftspaceId: string,
    credentials: S3Credentials,
    password: string,
  ): Promise<void> {
    const encrypted = await SecureCredentialStorage.encryptCredentials(credentials, password);
    const timestamp = Date.now();

    localStorage.setItem(SecureCredentialStorage.STORAGE_KEY + craftspaceId, encrypted);
    localStorage.setItem(SecureCredentialStorage.TIMESTAMP_KEY + craftspaceId, timestamp.toString());
  }

  static async getCredentials(
    craftspaceId: string,
    password: string,
  ): Promise<S3Credentials | null> {
    const encrypted = localStorage.getItem(SecureCredentialStorage.STORAGE_KEY + craftspaceId);
    const timestamp = localStorage.getItem(SecureCredentialStorage.TIMESTAMP_KEY + craftspaceId);

    if (!encrypted || !timestamp) return null;

    // Check if credentials have expired
    const age = Date.now() - parseInt(timestamp, 10);
    if (age > SecureCredentialStorage.CREDENTIAL_TIMEOUT) {
      SecureCredentialStorage.removeCredentials(craftspaceId);
      return null;
    }

    try {
      return await SecureCredentialStorage.decryptCredentials(encrypted, password);
    } catch (error) {
      console.error("Failed to decrypt credentials:", error);
      return null;
    }
  }

  static removeCredentials(craftspaceId: string): void {
    localStorage.removeItem(SecureCredentialStorage.STORAGE_KEY + craftspaceId);
    localStorage.removeItem(SecureCredentialStorage.TIMESTAMP_KEY + craftspaceId);
  }

  static clearAllCredentials(): void {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (
        key.startsWith(SecureCredentialStorage.STORAGE_KEY) ||
        key.startsWith(SecureCredentialStorage.TIMESTAMP_KEY)
      ) {
        localStorage.removeItem(key);
      }
    });
  }

  static hasCredentials(craftspaceId: string): boolean {
    return localStorage.getItem(SecureCredentialStorage.STORAGE_KEY + craftspaceId) !== null;
  }

  static getCredentialAge(craftspaceId: string): number | null {
    const timestamp = localStorage.getItem(SecureCredentialStorage.TIMESTAMP_KEY + craftspaceId);
    if (!timestamp) return null;

    return Date.now() - parseInt(timestamp, 10);
  }

  private static async encryptCredentials(
    credentials: S3Credentials,
    password: string,
  ): Promise<string> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Fix: Properly import the password as a CryptoKey with correct usages
    const passwordBuffer = new TextEncoder().encode(password);
    const baseKey = await crypto.subtle.importKey("raw", passwordBuffer, "PBKDF2", false, [
      "deriveKey",
    ]);

    const key = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: 100000,
        hash: "SHA-256",
      },
      baseKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt"],
    );

    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(JSON.stringify(credentials)),
    );

    return JSON.stringify({
      data: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
      salt: btoa(String.fromCharCode(...salt)),
      iv: btoa(String.fromCharCode(...iv)),
    });
  }

  private static async decryptCredentials(
    encryptedData: string,
    password: string,
  ): Promise<S3Credentials> {
    const { data, salt, iv } = JSON.parse(encryptedData);

    // Fix: Properly import the password as a CryptoKey with correct usages
    const passwordBuffer = new TextEncoder().encode(password);
    const baseKey = await crypto.subtle.importKey("raw", passwordBuffer, "PBKDF2", false, [
      "deriveKey",
    ]);

    const key = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: new Uint8Array(
          atob(salt)
            .split("")
            .map((c) => c.charCodeAt(0)),
        ),
        iterations: 100000,
        hash: "SHA-256",
      },
      baseKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"],
    );

    const decrypted = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: new Uint8Array(
          atob(iv)
            .split("")
            .map((c) => c.charCodeAt(0)),
        ),
      },
      key,
      new Uint8Array(
        atob(data)
          .split("")
          .map((c) => c.charCodeAt(0)),
      ),
    );

    return JSON.parse(new TextDecoder().decode(decrypted));
  }

  static validatePassword(password: string): boolean {
    // Require minimum 8 characters, at least one letter and one number
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  }

  static getPasswordStrength(password: string): "weak" | "medium" | "strong" {
    if (password.length < 8) return "weak";

    let score = 0;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[@$!%*?&]/.test(password)) score++;
    if (password.length >= 12) score++;

    if (score <= 2) return "weak";
    if (score <= 3) return "medium";
    return "strong";
  }
}
